import { Capacitor } from '@capacitor/core'
import { registerSW } from 'virtual:pwa-register'

export type PwaUpdateHandler = (reloadPage: () => Promise<void>) => void

/**
 * Registra o service worker do PWA apenas no browser em produção.
 * Com registerType: prompt, o app exibe diálogo antes de ativar nova versão.
 */
export function registerPwaServiceWorker(
  onNeedRefresh?: PwaUpdateHandler,
): (() => Promise<void>) | null {
  if (!import.meta.env.PROD || Capacitor.isNativePlatform()) {
    return null
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      onNeedRefresh?.(() => updateSW(true))
    },
  })

  return () => updateSW(true)
}

export async function activateWaitingServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false

  const registration = await navigator.serviceWorker.getRegistration()
  const waitingWorker = registration?.waiting
  if (!waitingWorker) return false

  await new Promise<void>((resolve) => {
    const timeoutId = window.setTimeout(resolve, 3000)

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(timeoutId)
        resolve()
      },
      { once: true },
    )

    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  })

  return true
}

async function clearAppCaches(): Promise<void> {
  if (!('caches' in window)) return

  try {
    const keys = await caches.keys()
    await Promise.all(keys.map((key) => caches.delete(key)))
  } catch {
    // ignore
  }
}

/**
 * Aplica a atualização de forma agressiva: ativa SW waiting, limpa Cache Storage
 * e navega com cache-buster (evita loop quando o reload suave reaproveita HTML stale).
 */
export async function forceAppUpdateReload(
  preferredReload?: (() => Promise<void>) | null,
): Promise<void> {
  if (preferredReload) {
    try {
      await preferredReload()
      return
    } catch {
      // fallback abaixo
    }
  }

  await activateWaitingServiceWorker()
  await clearAppCaches()

  const url = new URL(window.location.href)
  url.searchParams.set('_r', Date.now().toString(36))
  window.location.replace(url.toString())
}
